/**
 * Recent tables: the last five opened in the tool or put into a paper, newest
 * first. The insert sheet lists them above All (Olivia, tables-modern 1a).
 *
 * Kept in this browser. It is a convenience for finding a table again; losing
 * it (another device, cleared storage) costs nothing but the ordering.
 */

const KEY = "lilia:tables:recent";
export const RECENT_MAX = 5;

export function readRecentTables(): string[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    const ids = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string").slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

export function touchRecentTable(id: string): void {
  try {
    const next = [id, ...readRecentTables().filter((x) => x !== id)].slice(0, RECENT_MAX);
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage refused: the list simply is not remembered.
  }
}

/**
 * Recent, then All. With nothing remembered yet, the most recently edited
 * tables stand in, since `tables` arrives newest first. A list short enough to
 * see at once has no Recent: it would only repeat it.
 */
export function splitRecent<T extends { id: string }>(items: T[], remembered = readRecentTables()): { recent: T[]; rest: T[] } {
  if (items.length <= RECENT_MAX) return { recent: [], rest: items };
  const byId = new Map(items.map((t) => [t.id, t]));
  let recent = remembered.map((id) => byId.get(id)).filter((t): t is T => !!t);
  if (recent.length === 0) recent = items.slice(0, Math.min(3, items.length));
  const inRecent = new Set(recent.map((t) => t.id));
  return { recent, rest: items.filter((t) => !inRecent.has(t.id)) };
}
